import React, { useState, useEffect, useMemo } from 'react'

import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	TextField,
	MenuItem,
	Switch,
	Icon
} from '@material-ui/core'

import { isEmpty } from 'lodash'

import { useDispatch, useSelector } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'
import StyledAlert from '../../Common/StyledAlert'
import StyledSelect from '../../Common/StyledSelect'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { STATUS_ID, BOARDING } from '../../../utils'
import { updatePetVisitAssignments } from '../../../actions/visits/visits.actions'

const useStyles = makeStyles((theme) => ({
	dialogActions: {
		justifyContent: 'flex-start',
		padding: '0px 24px'
	},
	checkbox: {
		'&.Mui-disabled.Mui-checked .MuiIconButton-label .MuiSvgIcon-root': {
			fill: '#A2A1A4'
		}
	},
	avatar: {
		background: '#C4C4C4',
		height: '32px',
		width: '32px',
		marginTop: '12px',
		marginBottom: '12px'
	},
	textField: {
		marginBottom: '8px',
		'& .MuiTextField-root': {
			minWidth: '100%',
			marginBottom: '16px'
		},
		'& .MuiFilledInput-underline': {
			minWidth: '100%'
		}
	},
	sharingWrapper: {
		height: 56,
		width: '100%',
		padding: '0px 12px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderRadius: 6,
		background: '#F9F9F9'
	},
	switchRoot: {
		width: 51,
		height: 31,
		padding: 0
	},
	switchBase: {
		padding: 0,
		margin: 2,
		'&$checked': {
			transform: 'translateX(20px)',
			color: theme.palette.common.white,
			'& + $track': {
				backgroundColor: '#6B4AD6',
				opacity: 1
			},
			'& .far::before': {
				color: '#6B4AD6'
			}
		},
		'&$focusVisible $thumb': {
			color: '#6B4AD6'
			//border: "6px solid #fff",
		}
	},
	thumb: {
		width: 27,
		height: 27,
		background: '#F3F3F3',
		border: '1px solid #E5E5E5',
		borderRadius: '50%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& .far': {
			color: '#A2A1A4',
			fontSize: '13px',
			lineHeight: '15px'
		}
	},
	track: {
		borderRadius: 100,
		padding: 2,
		backgroundColor: '#D0CFD2',
		opacity: 1,
		transition: theme.transitions.create(['background-color'])
	},
	checked: {},
	kennelConflict: {
		backgroundColor: 'rgba(235, 76, 76, 0.1)',
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		padding: 12,
		borderRadius: 6,
		marginBottom: 24
	},
	kennelText: {
		color: '#000000',
		fontSize: 14,
		fontWeight: '600'
	}
}))

const KennelConflict = ({ name, restriction }) => {
	const classes = useStyles()

	return (
		<div className={classes.kennelConflict}>
			<i className="fa fa-exclamation-circle" style={{ color: '#EB4C4C', fontSize: 12, marginRight: 12 }} />
			<Typography variant="body" className={classes.kennelText}>
				{name} has a '{restriction}' pet care program restriction
			</Typography>
		</div>
	)
}

const EditAssignmentsDialog = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const [playgroup, setPlaygroup] = useState({})
	const [cubby, setCubby] = useState('')
	const [area, setArea] = useState({})
	const [kennel, setKennel] = useState({})
	const [kennelType, setKennelType] = useState(null)
	const [sharingKennels, setSharingKennels] = useState(false)
	const pets = useSelector((state) => state.specific.visits.pets)
	const [cubbies, setCubbies] = useState({})
	const [gearCubby, setGearCubby] = useState(-1)
	const [foodCubby, setFoodCubby] = useState(-1)
	const [medicationCubby, setMedicationCubby] = useState(-1)
	const [boardingCubby, setBoardingCubby] = useState(-1)
	const [busy, setBusy] = useState(false)

	const [availableCubbies, setAvailableCubbies] = useState({})

	const { getAvailableCubbiesForFacility } = useSelectedFacility()
	const { cubbies: petCubbies } = props
	let { isShown, setIsShown, pet, facility, visit, visits } = props

	const isAnActiveVisit = useMemo(() => {
		if (!visit || !visit.status) {
			return false
		}
		const { status } = visit
		return [STATUS_ID.Pending, STATUS_ID.Approved, STATUS_ID.InProgress].includes(status.id)
	}, [visit])

	useEffect(() => {
		const getCubbies = async () => {
			setBusy(true)
			const data = await getAvailableCubbiesForFacility(visit.dates[0])
			setAvailableCubbies(data)
			setBusy(false)
		}
		if (visit && visit.dates && facility) {
			getCubbies()
		}
	}, [visit, getAvailableCubbiesForFacility, facility])

	useEffect(() => {
		const { gear, food, medication, boarding } = petCubbies
		setCubbies({ ...petCubbies })
		if (gear) {
			setGearCubby(gear)
		}
		if (food) {
			setFoodCubby(food)
		}
		if (medication) {
			setMedicationCubby(medication)
		}
		if (boarding) {
			setBoardingCubby(boarding)
		}
	}, [petCubbies, isShown])

	const availableGearCubbies = useMemo(() => {
		if (Object.keys(availableCubbies).length === 0 || !availableCubbies?.gear?.standard) {
			return []
		}
		const opts = [{ label: 'None', value: -1 }]

		const gear = facility?.cubbies?.gear
		const prefix = gear?.prefix || 'G'
		let gearOptions = [...availableCubbies?.gear?.standard] || []

		if (cubbies?.gear && cubbies.gear !== null && isAnActiveVisit) {
			gearOptions.push(cubbies.gear)
		}

		gearOptions = gearOptions.sort((a, b) => a - b)

		gearOptions.forEach((cub) => {
			opts.push({
				label: `${prefix}${cub}`,
				value: cub
			})
		})
		return opts
	}, [availableCubbies, facility])

	const availableFoodCubbies = useMemo(() => {
		if (Object.keys(availableCubbies).length === 0 || !availableCubbies?.food?.standard) {
			return []
		}
		const opts = [{ label: 'None', value: -1 }]

		const food = facility?.cubbies?.food
		const prefix = food?.prefix || 'F'
		let foodOptions = [...availableCubbies?.food?.standard, ...availableCubbies?.food?.refrigerated] || []

		if (cubbies?.food && cubbies.food !== null && isAnActiveVisit) {
			foodOptions.push(cubbies.food)
		}

		foodOptions = foodOptions.sort((a, b) => a - b)

		foodOptions.forEach((cub) => {
			opts.push({
				label: `${prefix}${cub}`,
				value: cub
			})
		})
		return opts
	}, [availableCubbies, facility])

	const availableMedicationCubbies = useMemo(() => {
		if (Object.keys(availableCubbies).length === 0 || !availableCubbies?.medication?.standard) {
			return []
		}
		const opts = [{ label: 'None', value: -1 }]

		const medication = facility?.cubbies?.medication
		const prefix = medication?.prefix || 'G'
		let medicationOptions = [...availableCubbies?.medication?.standard] || []

		if (cubbies?.medication && cubbies.medication !== null && isAnActiveVisit) {
			medicationOptions.push(cubbies.medication)
		}

		medicationOptions = medicationOptions.sort((a, b) => a - b)

		medicationOptions.forEach((cub) => {
			opts.push({
				label: `${prefix}${cub}`,
				value: cub
			})
		})
		return opts
	}, [availableCubbies, facility])

	const availableBoardingCubbies = useMemo(() => {
		if (Object.keys(availableCubbies).length === 0 || !availableCubbies?.boarding?.standard) {
			return []
		}
		const opts = [{ label: 'None', value: -1 }]

		const boarding = facility?.cubbies?.boarding
		const prefix = boarding?.prefix || 'z'
		let boardingOptions = [...availableCubbies?.boarding?.standard] || []

		if (cubbies?.boarding && cubbies.boarding !== null && isAnActiveVisit) {
			boardingOptions.push(cubbies.boarding)
		}

		boardingOptions = boardingOptions.sort((a, b) => a - b)

		boardingOptions.forEach((cub) => {
			opts.push({
				label: `${prefix}${cub}`,
				value: cub
			})
		})
		return opts
	}, [availableCubbies, facility])

	const setInitialState = () => {
		setPlaygroup({})
		setCubby('')
		setGearCubby(petCubbies?.gear || -1)
		setFoodCubby(petCubbies?.food || -1)
		setMedicationCubby(petCubbies?.medication || -1)
		setBoardingCubby(petCubbies?.boarding || -1)
		setArea({})
		setKennel({})
		setKennelType(null)
		setSharingKennels(false)
	}

	const hasKennelConflict = kennelType && pet?.night_program_restrictions?.find((r) => r === kennelType)

	const getKennel = () => {
		if (facility && visit) {
			let { kennels, areas } = facility
			let area
			let kennel = kennels.find((k) => {
				let pet_ids = k?.pet_ids || []

				return pet_ids.includes(pet._id)
			})

			if (kennel) area = areas.find((a) => a._id === kennel.area_id)

			return { kennel: kennel, area: area }
		}

		return null
	}

	const isDifferent = () => {
		let originalPlaygroup =
			facility && pet.status && playgroup
				? facility.playgroups.find((playgroup) => pet.status.playgroup === playgroup._id)
				: null

		let kennelInfo = getKennel()

		if (originalPlaygroup && playgroup) {
			if (playgroup._id !== originalPlaygroup._id) return true
		}
		if (!originalPlaygroup && playgroup) return true
		let found = visit.boarding_options && visit.boarding_options.find((o) => o.pet_id === pet._id)

		if (kennelInfo && kennelInfo.kennel && kennelInfo.kennel._id !== kennel?._id) {
			return true
		}

		if (cubby && cubby !== (pet.status && pet.status.cubby ? pet.status.cubby : '')) {
			return true
		}

		if (!kennelInfo.kennel && kennel) return true
		if (Boolean(visit.sharing_kennels) !== sharingKennels) return true

		if (kennelType && (!found || !found.kennel_type || found.kennel_type !== kennelType)) return true

		if (
			(cubbies.gear !== gearCubby && gearCubby >= -1) ||
			(cubbies.food !== foodCubby && foodCubby >= -1) ||
			(cubbies.medication !== medicationCubby && medicationCubby >= -1) ||
			(cubbies.boarding !== boardingCubby && boardingCubby >= -1)
		) {
			return true
		}

		return false
	}

	const editAssignments = () => {
		let updates = {
			playgroup_id: playgroup ? playgroup._id : null,
			cubby: cubby ? cubby : '',
			kennel: kennel ? kennel : null,
			typeOfKennel: visit.type === 'boarding' ? 'night' : 'day',
			visit_id: visit._id,
			pet_id: pet._id,
			kennel_type: kennelType,
			sharing_kennels: sharingKennels
		}

		updates['cubbies'] = {
			gear: gearCubby === -1 ? null : gearCubby,
			food: foodCubby === -1 ? null : foodCubby,
			medication: medicationCubby === -1 ? null : medicationCubby,
			boarding: boardingCubby === -1 ? null : boardingCubby
		}

		dispatch(updatePetVisitAssignments(updates))
	}

	useEffect(() => {
		if (visit && pet && pet.status && facility && facility.playgroups) {
			setPlaygroup(facility.playgroups.find((playgroup) => pet.status.playgroup === playgroup._id))

			if (visit && visit.boarding_options && visit.boarding_options.length) {
				let foundIndex = visit.boarding_options.findIndex((o) => o.pet_id === pet._id)

				if (foundIndex !== -1) {
					setKennelType(visit.boarding_options[foundIndex].kennel_type || 'Standard')
				}
			}

			if (visit.hasOwnProperty('sharing_kennels')) setSharingKennels(visit.sharing_kennels)

			setCubby(pet.status.cubby ? pet.status.cubby : '')

			let { kennel, area } = getKennel()
			setArea(area)
			setKennel(kennel)
		}
	}, [pet, facility, visit, isShown])

	const hasBeef = useMemo(() => {
		const stati = [1, 2, 3, 4]

		if (playgroup?._id && visits?.length) {
			const dogsInGroup = []
			visits
				.filter((v) => stati.includes(v.status?.id))
				.forEach((v) => {
					if (v.pet_status) {
						v.pet_status.forEach((ps) => {
							if (ps.playgroup === playgroup._id || ps.playgroup_id == playgroup._id)
								dogsInGroup.push(ps.pet_id)
						})
					}
				})

			if (!!pet?.beefs?.find((p) => dogsInGroup.includes(p._id))) return true

			const mappedDogs = pets
				.filter((p) => dogsInGroup.includes(p._id))
				.filter((p) => p?.beefs?.length && p.beefs.find((b) => b._id == pet._id))

			return mappedDogs.length > 0 ? true : false
		}
	}, [pet, visit, visits, playgroup, pets])

	const handleGearUpdate = (e) => {
		setGearCubby(e.target.value)
	}

	const handleFoodUpdate = (e) => {
		setFoodCubby(e.target.value)
	}

	const handleMedicationUpdate = (e) => {
		setMedicationCubby(e.target.value)
	}

	const handleBoardingUpdate = (e) => {
		setBoardingCubby(e.target.value)
	}

	return (
		<Dialog
			open={isShown}
			className={classes.dialog}
			onClose={() => {
				setInitialState()
				setIsShown(false)
			}}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px',
					padding: '24px 0px'
				}
			}}>
			<DialogTitle
				style={{
					marginBottom: 18,
					padding: '0px 24px'
				}}>
				EDIT ASSIGNMENTS
			</DialogTitle>
			<DialogContent
				style={{
					padding: '0px 24px'
				}}>
				<Typography
					variant="overline"
					style={{
						marginBottom: 8
					}}>
					Playgroup
				</Typography>
				<div className={classes.textField}>
					<TextField
						select
						label="Playgroups"
						variant="filled"
						disabled={visit && visit.status.id === 5}
						value={playgroup}
						onChange={(e) => setPlaygroup(e.target.value)}>
						{facility && facility.playgroups
							? facility.playgroups.map((p) => (
									<MenuItem key={p._id} value={p}>
										{p.name}
									</MenuItem>
							  ))
							: []}
					</TextField>
					{hasBeef && (
						<StyledAlert
							type="error"
							text="This dog has a beef with another dog in this playgroup"
							style={{ marginBottom: 24 }}
						/>
					)}
				</div>
				<Typography
					variant="overline"
					style={{
						marginBottom: 8
					}}>
					Cubbies
				</Typography>
				<div
					className={classes.textField}
					style={{ gap: '1rem', display: 'flex', flexDirection: 'column' }}>
					<StyledSelect
						label="Gear Cubby"
						required={false}
						value={gearCubby}
						disabled={!isAnActiveVisit}
						onChange={handleGearUpdate}>
						{availableGearCubbies.map(({ label, value }) => {
							return (
								<MenuItem key={label} value={value}>
									{label}
								</MenuItem>
							)
						})}
					</StyledSelect>
					<StyledSelect
						label="Food Cubby"
						required={false}
						value={foodCubby}
						disabled={!isAnActiveVisit}
						onChange={handleFoodUpdate}>
						{availableFoodCubbies.map(({ label, value }) => {
							return (
								<MenuItem key={label} value={value}>
									{label}
								</MenuItem>
							)
						})}
					</StyledSelect>
					<StyledSelect
						label="Medication Cubby"
						required={false}
						value={medicationCubby}
						disabled={!isAnActiveVisit}
						onChange={handleMedicationUpdate}>
						{availableMedicationCubbies.map(({ label, value }) => {
							return (
								<MenuItem key={label} value={value}>
									{label}
								</MenuItem>
							)
						})}
					</StyledSelect>
					{visit?.type === BOARDING ? (
						<StyledSelect
							label="Boarding Cubby"
							required={false}
							value={boardingCubby}
							disabled={!isAnActiveVisit}
							onChange={handleBoardingUpdate}>
							{availableBoardingCubbies.map(({ label, value }) => {
								return (
									<MenuItem key={label} value={value}>
										{label}
									</MenuItem>
								)
							})}
						</StyledSelect>
					) : null}
				</div>
				<Typography
					variant="overline"
					style={{
						marginBottom: 8
					}}>
					Kennel
				</Typography>
				{visit.type === 'boarding' ? (
					<div className={classes.textField}>
						<TextField
							select
							label="Night Program"
							variant="filled"
							value={kennelType || 'Standard'}
							onChange={(e) => setKennelType(e.target.value)}>
							{facility && facility.kennel_options && facility.kennel_options.length
								? facility.kennel_options
										.filter((o) => !!o.active)
										.map((o) => (
											<MenuItem
												key={o.type}
												value={o.type}
												disabled={
													pet.kennel_free_permissions &&
													pet.kennel_free_permissions === 'Not Allowed' &&
													o.type === 'Kennel-Free'
												}>
												{o.type}
											</MenuItem>
										))
								: []}
						</TextField>
					</div>
				) : null}

				{hasKennelConflict && <KennelConflict name={pet.name} restriction={hasKennelConflict} />}
				<div className={classes.textField} style={{ marginBottom: '32px' }}>
					{/* <TextField
            select
            label="Kennel Room"
            variant="filled"
            disabled={visit && visit.status.id === 5}
            value={area}
            onChange={(e) => setArea(e.target.value)}
          >
            {facility && facility.areas
              ? facility.areas.map((p) => (
                  <MenuItem key={p._id} value={p}>
                    {p.name}
                  </MenuItem>
                ))
              : []}
          </TextField> */}
					<TextField
						select
						label="Kennel"
						variant="filled"
						disabled={visit && visit.status.id === 5}
						value={kennel}
						onChange={(e) => setKennel(e.target.value)}>
						{(facility &&
							facility?.kennels
								?.sort((a, b) => a.reference_number - b.reference_number)
								.map((k) => (
									<MenuItem key={k._id} value={k}>
										{k.reference_number}
									</MenuItem>
								))) ||
							[]}
					</TextField>
					{visit.type === 'boarding' && visit.pet_ids && visit.pet_ids.length > 1 ? (
						<div className={classes.sharingWrapper}>
							<Typography>Sharing with sibling</Typography>
							<Switch
								checked={sharingKennels}
								onChange={() => {
									setSharingKennels(!sharingKennels)
								}}
								name="sharingKennels"
								icon={
									<div className={classes.thumb}>
										<Icon className="far fa-times" />
									</div>
								}
								checkedIcon={
									<div className={classes.thumb}>
										<Icon className="far fa-check" />
									</div>
								}
								classes={{
									root: classes.switchRoot,
									switchBase: classes.switchBase,
									thumb: classes.thumb,
									track: classes.track,
									checked: classes.checked
								}}
							/>
						</div>
					) : null}
				</div>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					disabled={!isDifferent()}
					variant="contained"
					onClick={() => {
						setIsShown(false)
						editAssignments()
					}}
					color="secondary">
					Save
				</Button>
				<Button
					variant="outlined"
					onClick={() => {
						setInitialState()
						setIsShown(false)
					}}
					color="secondary">
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default EditAssignmentsDialog
